import React from 'react';
import { ThumbsUp, ThumbsDown, Share2, MoreHorizontal, MessageCircle } from 'lucide-react';
import { MediaDisplay } from './MediaDisplay';
import { PreviewProps, getFollowersCount, formatFollowersCount } from './types';

// Helper function to check if URL is a video
const isVideo = (url?: string): boolean => {
  if (!url) return false;
  const extension = url.split('.').pop()?.toLowerCase();
  return ['mp4', 'mov', 'webm'].includes(extension || '');
};

export const YouTubePreview: React.FC<PreviewProps> = ({ content, brand, mediaUrl, format = 'regular' }) => {
  // Get the YouTube account from brand's social accounts if available
  const getYouTubeAccount = () => {
    if (brand?.social_accounts && Array.isArray(brand.social_accounts)) {
      return brand.social_accounts.find(
        account => account.platform.toLowerCase() === 'youtube'
      );
    }
    return null;
  };
  
  const youtubeAccount = getYouTubeAccount();
  
  // Récupérer le nombre de followers YouTube pour des valeurs par défaut plus réalistes
  const youtubeFollowers = getFollowersCount(brand, 'youtube');
  
  // Calculer des valeurs par défaut basées sur les followers (engagement YouTube typique ~2-5%)
  const defaultLikes = youtubeFollowers ? Math.floor(youtubeFollowers * 0.03) : 23000;
  const defaultComments = youtubeFollowers ? Math.floor(youtubeFollowers * 0.01) : 1893;
  const defaultSubscribers = youtubeFollowers || 126000;
  
  // Get the YouTube username
  const getYouTubeUsername = (): string => {
    if (youtubeAccount?.username) {
      return youtubeAccount.username;
    }
    
    // Fallback to the old behavior if no YouTube account found
    return brand?.name || 'companyname';
  };
  
  // Get the profile picture
  const getProfilePicture = (): string => {
    if (youtubeAccount?.picture_url) {
      return youtubeAccount.picture_url;
    }
    
    // Fallback to the brand logo
    return brand?.logo || '';
  };

  // Regular YouTube video
  if (format === 'regular') {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden w-full">
        {/* Video */}
        <div className="w-full bg-gray-100 aspect-video">
          <MediaDisplay
            mediaUrl={mediaUrl}
            altText="YouTube video"
            type="video"
            className="w-full h-full"
          />
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Title */}
          <h3 className="font-semibold text-lg mb-2 line-clamp-2">
            {content.split('\n')[0] || "Video Title"}
          </h3>

          {/* Channel info */}
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
              {getProfilePicture() ? (
                <img src={getProfilePicture()} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-200" />
              )}
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm">{getYouTubeUsername()}</div>
              <div className="text-xs text-gray-600">{formatFollowersCount(defaultSubscribers)} subscribers</div>
            </div>
            <button className="bg-black text-white px-4 py-2 rounded-full text-sm font-medium">
              Subscribe
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 border-t border-b border-gray-200 py-2">
            <div className="flex items-center mr-3">
              <button className="flex items-center gap-1 bg-gray-100 rounded-l-full px-4 py-1.5">
                <ThumbsUp className="w-5 h-5" />
                <span className="text-sm">{formatFollowersCount(defaultLikes)}</span>
              </button>
              <button className="bg-gray-100 rounded-r-full px-4 py-1.5">
                <ThumbsDown className="w-5 h-5" />
              </button>
            </div>
            
            <button className="flex items-center gap-1 bg-gray-100 rounded-full px-4 py-1.5">
              <Share2 className="w-5 h-5" />
              <span className="text-sm">Share</span>
            </button>
            
            <button className="flex items-center gap-1 bg-gray-100 rounded-full px-4 py-1.5 ml-auto">
              <MoreHorizontal className="w-5 h-5" />
            </button>
          </div>

          {/* Description */}
          <div className="mt-3 text-sm">
            <p className="whitespace-pre-line">
              {content.split('\n').slice(1).join('\n') || "Video description goes here..."}
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // YouTube Shorts
  return (
    <div className="bg-black text-white rounded-xl shadow-md border border-gray-800 overflow-hidden max-w-[320px] mx-auto">
      <div className="relative w-full aspect-[9/16] bg-gray-900 flex flex-col">
        {/* Main content */}
        <div className="flex-1 relative">
          <MediaDisplay
            mediaUrl={mediaUrl}
            altText="YouTube Shorts"
            type="video"
            fullSize={false}
            className="h-full w-full object-cover"
            controls={false}
            autoPlayOnMount
            loop
            muted
            aspectRatio="9/16"
            key={mediaUrl}
          />
          
          {/* Right side actions */}
          <div className="absolute right-3 bottom-20 flex flex-col items-center space-y-6">
            <button className="flex flex-col items-center">
              <ThumbsUp className="w-7 h-7" />
              <span className="text-xs mt-1">{formatFollowersCount(defaultLikes)}</span>
            </button>
            <button className="flex flex-col items-center">
              <ThumbsDown className="w-7 h-7" />
              <span className="text-xs mt-1">Dislike</span>
            </button>
            <button className="flex flex-col items-center">
              <MessageCircle className="w-7 h-7" />
              <span className="text-xs mt-1">{formatFollowersCount(defaultComments)}</span>
            </button>
            <button className="flex flex-col items-center">
              <Share2 className="w-7 h-7" />
              <span className="text-xs mt-1">Share</span>
            </button>
            <button className="flex flex-col items-center">
              <MoreHorizontal className="w-7 h-7" />
            </button>
          </div>

          {/* Bottom caption */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
            <p className="mb-3 whitespace-pre-line" style={{ fontSize: '0.8rem' }}>{content}</p>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gray-100 overflow-hidden">
                {getProfilePicture() ? (
                  <img src={getProfilePicture()} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-200" />
                )}
              </div>
              <span className="font-medium text-sm">@{getYouTubeUsername()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};